/**
 * =======================================================
 * SISTEMA DE PREFETCH DE DADOS
 * =======================================================
 * 
 * Pré-carrega dados do backend enquanto usuário está no
 * dashboard, complementando o prefetch de componentes.
 * 
 * -------------------------------------------------------
 * COMO ADICIONAR DADOS DE NOVA FERRAMENTA:
 * -------------------------------------------------------
 * 
 * 1. Importar a função de fetch do service correspondente
 * 2. Adicionar staleTime em STALE_TIMES (se necessário)
 * 3. Adicionar prefetchQuery com queryKey único dentro de prefetchAll()
 * 
 * Exemplo:
 * queryClient.prefetchQuery({
 *   queryKey: ['nova-ferramenta-data'],
 *   queryFn: fetchNovaFerramentaData,
 *   staleTime: STALE_TIMES.novaFerramenta,
 * });
 * 
 * -------------------------------------------------------
 * STALE TIMES RECOMENDADOS:
 * -------------------------------------------------------
 * - Dados que mudam frequentemente: 5 min
 * - Dados moderadamente estáveis: 10-30 min
 * - Dados raramente alterados: 1 hora+
 * 
 * =======================================================
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useRole } from '@/contexts/RoleContext';
import { fetchListings } from '@/services/marketplace';
import { fetchTerminalMetrics, fetchAdminUsers } from '@/services/branding';
import { fetchToolSettings } from '@/services/toolSettings';
import { TOOL_SETTINGS_KEY } from '@/hooks/useToolSettings';
import { fetchFolders, fetchNotes } from '@/services/forjaNotes';
import { fetchWorkspaces } from '@/services/forjaTasks';
import { fetchActivePartners, fetchAllPartnersForAdmin } from '@/services/partners';
import { fetchActiveMembersGroupedDefault } from '@/services/communityMembers';
import { fetchNegotiationsCounts } from '@/services/marketplace';
import { fetchToolAverageCost } from '@/services/sparks';
import { CURRENT_USER_REPUTATION_KEY, fetchCurrentUserReputation } from '@/hooks/useCurrentUserReputation';
import { fetchCommunityTabs, fetchPosts } from '@/services/community';
import { COMMUNITY_KEYS } from '@/services/community/queryKeys';
import { fetchUserGamificationSummary, GAMIFICATION_KEYS } from '@/services/communityGamification';
import { fetchInitialMessages } from '@/components/comunidade/hooks/useChatMessages';


// Stale times for different data types
const STALE_TIMES = {
  marketplace: 5 * 60 * 1000,      // 5 minutes
  negotiations: 30 * 60 * 1000,    // 30 minutes
  terminal: 60 * 60 * 1000,        // 1 hour
  adminUsers: 10 * 60 * 1000,      // 10 minutes
  toolSettings: 30 * 60 * 1000,    // 30 minutes
  forjaNotes: 5 * 60 * 1000,       // 5 minutes
  forjaTasks: 5 * 60 * 1000,       // 5 minutes
  partners: 5 * 60 * 1000,         // 5 minutes
  reputation: 10 * 60 * 1000,      // 10 minutes
  toolCosts: 30 * 60 * 1000,       // 30 minutes
  communityTabs: 5 * 60 * 1000,    // 5 minutes
  gamification: 10 * 60 * 1000,    // 10 minutes
  communityChat: 2 * 60 * 1000,     // 2 minutes
  communityPosts: 30 * 1000,        // 30 seconds
  communityMembers: 5 * 60 * 1000,  // 5 minutes
};

export function useEagerPrefetch() {
  const { user } = useAuth();
  const { effectiveRole } = useRole();
  const queryClient = useQueryClient();
  const hasPrefetched = useRef(false);

  useEffect(() => {
    // Only prefetch once per session and only for logged-in users
    if (!user || hasPrefetched.current) return;

    const prefetchAll = () => {
      // Marketplace listings - all users benefit from this
      queryClient.prefetchQuery({
        queryKey: ['marketplace-listings', '{}'],
        queryFn: () => fetchListings({}),
        staleTime: STALE_TIMES.marketplace,
      });

      // Negotiations counts - unified single query
      queryClient.prefetchQuery({
        queryKey: ['negotiations-counts'],
        queryFn: fetchNegotiationsCounts,
        staleTime: STALE_TIMES.negotiations,
      });

      // Tool settings - everyone needs this
      queryClient.prefetchQuery({
        queryKey: TOOL_SETTINGS_KEY,
        queryFn: fetchToolSettings,
        staleTime: STALE_TIMES.toolSettings,
      });

      // Forja Notes data - prefetch for instant loading
      queryClient.prefetchQuery({
        queryKey: ['forja-notes-folders'],
        queryFn: fetchFolders,
        staleTime: STALE_TIMES.forjaNotes,
      });

      queryClient.prefetchQuery({
        queryKey: ['forja-notes'],
        queryFn: fetchNotes,
        staleTime: STALE_TIMES.forjaNotes,
      });

      // Forja Tasks data - prefetch workspaces
      queryClient.prefetchQuery({
        queryKey: ['forja-tasks-workspaces'],
        queryFn: fetchWorkspaces,
        staleTime: STALE_TIMES.forjaTasks,
      });

      // Partners data - prefetch for instant loading
      queryClient.prefetchQuery({
        queryKey: ['partners-active'],
        queryFn: fetchActivePartners,
        staleTime: STALE_TIMES.partners,
      });

      // Community members sidebar - prefetch for instant loading
      queryClient.prefetchQuery({
        queryKey: COMMUNITY_KEYS.members(),
        queryFn: fetchActiveMembersGroupedDefault,
        staleTime: STALE_TIMES.communityMembers,
      });

      // Community tabs - prefetch for instant Comunidade loading
      // Then chain: prefetch chat messages and forum posts for all tabs
      queryClient.prefetchQuery({
        queryKey: ['community-tabs'],
        queryFn: fetchCommunityTabs,
        staleTime: STALE_TIMES.communityTabs,
      }).then(() => {
        const tabs = queryClient.getQueryData<any[]>(['community-tabs']);
        if (!tabs?.length) return;

        // Prefetch chat messages for all chat tabs
        for (const tab of tabs.filter((t: any) => t.tab_type === 'chat')) {
          queryClient.prefetchQuery({
            queryKey: ['community-chat-messages', tab.slug],
            queryFn: () => fetchInitialMessages(tab.slug),
            staleTime: STALE_TIMES.communityChat,
          });
        }

        // Prefetch forum/ensino posts for first 3 content tabs
        for (const tab of tabs.filter((t: any) => t.tab_type === 'forum' || t.tab_type === 'ensino').slice(0, 3)) {
          queryClient.prefetchQuery({
            queryKey: ['community-posts', tab.slug, 1, 10, null],
            queryFn: () => fetchPosts(tab.slug, { page: 1, pageSize: 10 }),
            staleTime: STALE_TIMES.communityPosts,
          });
        }
      });

      // Gamification summary - prefetch for instant Comunidade loading
      queryClient.prefetchQuery({
        queryKey: GAMIFICATION_KEYS.summary(user.id),
        queryFn: () => fetchUserGamificationSummary(user.id),
        staleTime: STALE_TIMES.gamification,
      });

      // User reputation - prefetch using unified key
      queryClient.prefetchQuery({
        queryKey: [CURRENT_USER_REPUTATION_KEY, user.id],
        queryFn: () => fetchCurrentUserReputation(user.id),
        staleTime: STALE_TIMES.reputation,
      });

      // Tool costs - prefetch for instant tool loading
      queryClient.prefetchQuery({
        queryKey: ['tool-cost', 'downloader'],
        queryFn: () => fetchToolAverageCost('downloader'),
        staleTime: STALE_TIMES.toolCosts,
      });
      queryClient.prefetchQuery({
        queryKey: ['tool-cost', 'transcription'],
        queryFn: () => fetchToolAverageCost('transcription'),
        staleTime: STALE_TIMES.toolCosts,
      });
      queryClient.prefetchQuery({
        queryKey: ['tool-cost', 'gmv-creator-lookup'],
        queryFn: () => fetchToolAverageCost('gmv-creator-lookup'),
        staleTime: STALE_TIMES.toolCosts,
      });

      // Admin-only data
      if (effectiveRole === 'admin' || effectiveRole === 'staff') {
        // Terminal metrics
        queryClient.prefetchQuery({
          queryKey: ['terminal-metrics'],
          queryFn: fetchTerminalMetrics,
          staleTime: STALE_TIMES.terminal,
        });

        // Admin users list
        queryClient.prefetchQuery({
          queryKey: ['admin-users'],
          queryFn: fetchAdminUsers,
          staleTime: STALE_TIMES.adminUsers,
        });

        // Admin partners list
        queryClient.prefetchQuery({
          queryKey: ['admin', 'partners-all'],
          queryFn: fetchAllPartnersForAdmin,
          staleTime: STALE_TIMES.adminUsers,
        });
      }

      hasPrefetched.current = true;
    };

    // Execute immediately - no delays for instant loading
    prefetchAll();
  }, [user, effectiveRole, queryClient]);
}
